import React, { useEffect, useState, useCallback, useRef, useContext } from "react";
import { PermissionsContext } from "../../utils/PermissionsContext";
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Alert, Tooltip, Tab, Tabs, InputAdornment, Switch,
  FormControlLabel, Divider, Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";
import OwnerInfoPopover from "./OwnerInfoPopover";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import api from "../../utils/api";

/* ── Styles ─────────────────────────────────────────────────────────────── */
const primaryBtnSx = {
  backgroundColor: "#2f63ad", color: "#fff", fontWeight: 700, borderRadius: "10px",
  textTransform: "none", boxShadow: "none",
  "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
};
const secondaryBtnSx = { borderRadius: "10px", textTransform: "none", fontWeight: 600 };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" } };
const dialogPaperSx = { borderRadius: "18px", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" };

const VESSEL_TYPES = ["powerboat", "sailboat", "catamaran", "trawler", "yacht", "personal watercraft", "other"];
const POWER_OPTIONS = ["none", "30amp", "50amp", "100amp"];
// Normalize to YYYY-MM-DD before parsing. handles both "2026-05-29" and "2026-05-29T00:00:00.000Z"
const toDateStr = (d) => d ? String(d).slice(0, 10) : null;
const fmtDate   = (d) => { const s = toDateStr(d); if (!s) return null; return new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };
const daysUntil = (d) => { const s = toDateStr(d); if (!s) return null; return Math.ceil((new Date(s + "T00:00:00") - new Date()) / 86400000); };

/* ── Vessel dialog ──────────────────────────────────────────────────────── */
const BLANK_VESSEL = {
  vessel_name: "", owner_type: "external", owner_id: "", property_id: "",
  registration_number: "", documentation_number: "", vessel_type: "",
  make: "", model: "", year: "", loa: "", beam: "", draft: "", air_draft: "",
  power_required: "none", color: "",
  insurance_company: "", insurance_policy: "", insurance_expiry: "",
  registration_expiry: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  notes: "", is_active: true,
};

const VesselDialog = ({ open, editing, onClose, onSaved }) => {
  const [form, setForm] = useState(BLANK_VESSEL);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [owners, setOwners] = useState([]);
  const [ownerSearch, setOwnerSearch] = useState("");
  const ownerDebounce = useRef(null);
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const memberDebounce = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...BLANK_VESSEL, ...editing, is_active: Boolean(editing.is_active ?? 1) } : BLANK_VESSEL);
      setErr("");
      setOwnerSearch(""); setOwners([]);
      setMemberSearch(""); setMembers([]); setSelectedMember(null);
    }
  }, [open, editing]);

  useEffect(() => {
    if (form.owner_type !== "external" || !ownerSearch.trim()) { setOwners([]); return; }
    clearTimeout(ownerDebounce.current);
    ownerDebounce.current = setTimeout(async () => {
      try {
        const res = await api.get(`/vessel-owners?search=${encodeURIComponent(ownerSearch)}`);
        setOwners(res.data || []);
      } catch { /* silent */ }
    }, 350);
    return () => clearTimeout(ownerDebounce.current);
  }, [ownerSearch, form.owner_type]);

  useEffect(() => {
    if (form.owner_type !== "hoa_member" || !memberSearch.trim()) { setMembers([]); return; }
    clearTimeout(memberDebounce.current);
    memberDebounce.current = setTimeout(async () => {
      try {
        const res = await api.get(`/membersfilter?search=${encodeURIComponent(memberSearch)}`);
        setMembers(res.data?.members || []);
      } catch { /* silent */ }
    }, 400);
    return () => clearTimeout(memberDebounce.current);
  }, [memberSearch, form.owner_type]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.vessel_name.trim()) { setErr("Vessel name is required."); return; }
    if (form.owner_type === "external" && !form.owner_id) { setErr("Select an external owner."); return; }
    if (form.owner_type === "hoa_member" && !form.property_id) { setErr("Select an HOA member to set the property ID."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        is_active: form.is_active ? 1 : 0,
        owner_id: form.owner_type === "external" ? (form.owner_id || null) : null,
        property_id: form.owner_type === "hoa_member" ? (form.property_id || null) : null,
        loa: form.loa || null, beam: form.beam || null, draft: form.draft || null,
        air_draft: form.air_draft || null, year: form.year || null,
      };
      if (editing?.id) await api.put(`/vessels/${editing.id}`, payload);
      else await api.post("/vessels", payload);
      onSaved();
      onClose();
    } catch { setErr("Failed to save vessel."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ fontWeight: 700, pt: 3, pb: 1 }}>{editing ? "Edit Vessel" : "Add Vessel"}</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={8}>
            <TextField fullWidth label="Vessel Name *" value={form.vessel_name} onChange={set("vessel_name")} sx={fieldSx} />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Owner Type</InputLabel>
              <Select value={form.owner_type} label="Owner Type" onChange={set("owner_type")}>
                <MenuItem value="external">External Owner</MenuItem>
                <MenuItem value="hoa_member">HOA Member</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Owner lookup */}
          {form.owner_type === "external" ? (
            <Grid item xs={12}>
              <TextField fullWidth label="Search External Owner" placeholder="Type to search…"
                value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} /></InputAdornment> }}
                sx={fieldSx} />
              {owners.length > 0 && (
                <Paper elevation={2} sx={{ mt: 0.5, borderRadius: "10px", overflow: "hidden", maxHeight: 160, overflowY: "auto" }}>
                  {owners.map((o) => (
                    <Box
                      key={o.id} onClick={() => { setForm((p) => ({ ...p, owner_id: o.id })); setOwnerSearch(`${o.first_name} ${o.last_name}`); setOwners([]); }}
                      sx={{ px: 2, py: 1, cursor: "pointer", "&:hover": { backgroundColor: "rgba(47,99,173,0.06)" }, borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.first_name} {o.last_name}</Typography>
                      <Typography variant="caption" sx={{ color: "#6b778c" }}>{o.email}</Typography>
                    </Box>
                  ))}
                </Paper>
              )}
              {form.owner_id && <Typography variant="caption" sx={{ color: "#16a34a", ml: 1 }}>✓ Owner selected (ID {form.owner_id})</Typography>}
            </Grid>
          ) : (
            <Grid item xs={12}>
              {selectedMember ? (
                /* ── Confirmed member card ── */
                <Box>
                  <Box sx={{ p: 1.5, borderRadius: "10px", backgroundColor: "rgba(47,99,173,0.05)", border: "1px solid rgba(47,99,173,0.2)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>
                          {selectedMember.FirstName} {selectedMember.LastName}
                          {selectedMember.FamiliarName && selectedMember.FamiliarName !== selectedMember.FirstName
                            ? <Box component="span" sx={{ fontWeight: 400, color: "#6b778c" }}> "{selectedMember.FamiliarName}"</Box>
                            : null}
                        </Typography>
                        <Chip label={`#${selectedMember.ClubMemberCode}`} size="small"
                          sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, fontFamily: "monospace",
                            backgroundColor: "rgba(47,99,173,0.12)", color: "#2f63ad" }} />
                      </Box>
                      {selectedMember.FullAddress && (
                        <Typography variant="caption" sx={{ color: "#6b778c" }}>{selectedMember.FullAddress}</Typography>
                      )}
                      {selectedMember.EmailAddress && (
                        <Typography variant="caption" sx={{ display: "block", color: "#9aa5b4" }}>{selectedMember.EmailAddress}</Typography>
                      )}
                    </Box>
                    <Button size="small" variant="outlined"
                      onClick={() => { setSelectedMember(null); setMemberSearch(""); setForm((p) => ({ ...p, property_id: "" })); }}
                      sx={{ borderRadius: "8px", textTransform: "none", fontSize: "0.75rem", whiteSpace: "nowrap", flexShrink: 0 }}>
                      Change
                    </Button>
                  </Box>
                  <Typography variant="caption" sx={{ color: "#16a34a", ml: 0.5 }}>✓ Property ID set to {form.property_id}</Typography>
                </Box>
              ) : (
                /* ── Member search typeahead ── */
                <Box>
                  <TextField fullWidth label="Search HOA Member *" placeholder="Name, member code, or address…"
                    value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} /></InputAdornment> }}
                    sx={fieldSx} />
                  {members.length > 0 && (
                    <Paper elevation={3} sx={{ mt: 0.5, borderRadius: "10px", overflow: "hidden", maxHeight: 240, overflowY: "auto" }}>
                      {members.map((m) => (
                        <Box key={m.ClubMemberCode}
                          onClick={() => {
                            setSelectedMember(m);
                            setMemberSearch("");
                            setMembers([]);
                            setForm((p) => ({ ...p, property_id: m.ClubMemberCode }));
                          }}
                          sx={{ px: 2, py: 1, cursor: "pointer", borderBottom: "1px solid #f0f0f0",
                            "&:hover": { backgroundColor: "rgba(47,99,173,0.05)" } }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>
                              {m.FirstName} {m.LastName}
                              {m.FamiliarName && m.FamiliarName !== m.FirstName
                                ? <Box component="span" sx={{ fontWeight: 400, color: "#9aa5b4" }}> "{m.FamiliarName}"</Box>
                                : null}
                            </Typography>
                            <Chip label={`#${m.ClubMemberCode}`} size="small"
                              sx={{ height: 18, fontSize: "0.68rem", fontWeight: 700, fontFamily: "monospace",
                                backgroundColor: "rgba(47,99,173,0.1)", color: "#2f63ad", flexShrink: 0 }} />
                          </Box>
                          {m.FullAddress && (
                            <Typography variant="caption" sx={{ color: "#9aa5b4" }}>{m.FullAddress}</Typography>
                          )}
                        </Box>
                      ))}
                    </Paper>
                  )}
                  {/* When editing: show current property_id as a hint */}
                  {editing?.property_id && !selectedMember && (
                    <Typography variant="caption" sx={{ color: "#6b778c", ml: 0.5, mt: 0.5, display: "block" }}>
                      Current: <strong>#{editing.property_id}</strong>. search above to change, or leave as-is
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
          )}

          {/* Vessel info */}
          <Grid item xs={4}>
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Vessel Type</InputLabel>
              <Select value={form.vessel_type} label="Vessel Type" onChange={set("vessel_type")}>
                <MenuItem value="">-</MenuItem>
                {VESSEL_TYPES.map((t) => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Make" value={form.make} onChange={set("make")} sx={fieldSx} />
          </Grid>
          <Grid item xs={2}>
            <TextField fullWidth label="Model" value={form.model} onChange={set("model")} sx={fieldSx} />
          </Grid>
          <Grid item xs={2}>
            <TextField fullWidth label="Year" type="number" value={form.year} onChange={set("year")} sx={fieldSx} />
          </Grid>

          {/* Dimensions */}
          <Grid item xs={3}>
            <TextField fullWidth label="LOA (ft)" type="number" value={form.loa} onChange={set("loa")} sx={fieldSx} />
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth label="Beam (ft)" type="number" value={form.beam} onChange={set("beam")} sx={fieldSx} />
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth label="Draft (ft)" type="number" value={form.draft} onChange={set("draft")} sx={fieldSx} />
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Power Required</InputLabel>
              <Select value={form.power_required} label="Power Required" onChange={set("power_required")}>
                {POWER_OPTIONS.map((p) => <MenuItem key={p} value={p}>{p === "none" ? "None" : p}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          {/* Registration */}
          <Grid item xs={6}>
            <TextField fullWidth label="Registration #" value={form.registration_number} onChange={set("registration_number")} sx={fieldSx} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth type="date" label="Registration Expiry" value={form.registration_expiry} onChange={set("registration_expiry")} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          </Grid>

          {/* Insurance */}
          <Grid item xs={4}>
            <TextField fullWidth label="Insurance Company" value={form.insurance_company} onChange={set("insurance_company")} sx={fieldSx} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Policy #" value={form.insurance_policy} onChange={set("insurance_policy")} sx={fieldSx} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth type="date" label="Insurance Expiry" value={form.insurance_expiry} onChange={set("insurance_expiry")} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          </Grid>

          {/* Emergency contact */}
          <Grid item xs={6}>
            <TextField fullWidth label="Emergency Contact Name" value={form.emergency_contact_name} onChange={set("emergency_contact_name")} sx={fieldSx} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Emergency Contact Phone" value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} sx={fieldSx} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Notes" multiline rows={2} value={form.notes} onChange={set("notes")} sx={fieldSx} />
          </Grid>
          {editing && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} color="success" />}
                label="Active"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1.25 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" sx={primaryBtnSx} disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : editing ? "Update" : "Add Vessel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Owner dialog ───────────────────────────────────────────────────────── */
const BLANK_OWNER = { first_name: "", last_name: "", email: "", phone: "", address: "", city: "", state: "", zip: "", notes: "", is_active: true };

const OwnerDialog = ({ open, editing, onClose, onSaved }) => {
  const [form, setForm] = useState(BLANK_OWNER);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...BLANK_OWNER, ...editing, is_active: Boolean(editing.is_active ?? 1) } : BLANK_OWNER);
      setErr("");
    }
  }, [open, editing]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { setErr("First and last name are required."); return; }
    setSaving(true);
    try {
      const payload = { ...form, is_active: form.is_active ? 1 : 0 };
      if (editing?.id) await api.put(`/vessel-owners/${editing.id}`, payload);
      else await api.post("/vessel-owners", payload);
      onSaved();
      onClose();
    } catch { setErr("Failed to save owner."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ fontWeight: 700, pt: 3, pb: 1 }}>{editing ? "Edit Owner" : "Add External Owner"}</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}><TextField fullWidth label="First Name *" value={form.first_name} onChange={set("first_name")} sx={fieldSx} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Last Name *" value={form.last_name} onChange={set("last_name")} sx={fieldSx} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Email" value={form.email} onChange={set("email")} sx={fieldSx} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Phone" value={form.phone} onChange={set("phone")} sx={fieldSx} /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Address" value={form.address} onChange={set("address")} sx={fieldSx} /></Grid>
          <Grid item xs={5}><TextField fullWidth label="City" value={form.city} onChange={set("city")} sx={fieldSx} /></Grid>
          <Grid item xs={3}><TextField fullWidth label="State" value={form.state} onChange={set("state")} sx={fieldSx} /></Grid>
          <Grid item xs={4}><TextField fullWidth label="Zip" value={form.zip} onChange={set("zip")} sx={fieldSx} /></Grid>
          <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Notes" value={form.notes} onChange={set("notes")} sx={fieldSx} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1.25 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" sx={primaryBtnSx} disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : editing ? "Update" : "Add Owner"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Expiry chip ────────────────────────────────────────────────────────── */
const ExpiryChip = ({ dateStr }) => {
  const days = daysUntil(dateStr);
  if (!dateStr) return <Typography variant="caption" sx={{ color: "#9aa5b4" }}>-</Typography>;
  const expired = days < 0;
  const soon = days >= 0 && days <= 30;
  const color = expired ? "#dc2626" : soon ? "#d97706" : "#16a34a";
  const bg = expired ? "rgba(220,38,38,0.1)" : soon ? "rgba(217,119,6,0.1)" : "rgba(22,163,74,0.1)";
  return (
    <Chip
      icon={soon || expired ? <WarningAmberIcon sx={{ fontSize: "14px !important", color: `${color} !important` }} /> : undefined}
      label={expired ? `Expired ${Math.abs(days)}d ago` : soon ? `${days}d left` : fmtDate(dateStr)}
      size="small"
      sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, backgroundColor: bg, color }}
    />
  );
};

/* ── Vessel History Dialog ──────────────────────────────────────────────── */
const RES_STATUS_COLORS = {
  confirmed:   { bg: "rgba(47,99,173,0.1)",   color: "#1d4ed8" },
  pending:     { bg: "rgba(217,119,6,0.1)",   color: "#b45309" },
  checked_in:  { bg: "rgba(22,163,74,0.1)",   color: "#15803d" },
  checked_out: { bg: "rgba(107,114,128,0.1)", color: "#374151" },
  cancelled:   { bg: "rgba(220,38,38,0.1)",   color: "#dc2626" },
  no_show:     { bg: "rgba(127,29,29,0.1)",   color: "#7f1d1d" },
};

const VesselHistoryDialog = ({ open, vessel, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!open || !vessel) return;
    setExpanded({});
    setLoading(true);
    setData(null);
    api.get(`/vessels/${vessel.id}/history`)
      .then((r) => setData(r.data))
      .catch(() => {/* silent */})
      .finally(() => setLoading(false));
  }, [open, vessel]);

  const toggleRow = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const fmtDateLocal = (d) => {
    if (!d) return "N/A";
    return new Date(String(d).slice(0, 10) + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const summary = data?.summary;
  const reservations = data?.reservations || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ fontWeight: 700, pt: 3 }}>
        Vessel History
        {vessel && (
          <Typography variant="body2" sx={{ color: "#6b778c", fontWeight: 400, mt: 0.25 }}>
            {vessel.vessel_name}
            {vessel.registration_number ? ` · ${vessel.registration_number}` : ""}
            {vessel.owner_display ? ` · ${vessel.owner_display}` : ""}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : !data ? (
          <Alert severity="info" sx={{ borderRadius: "10px" }}>No history data available.</Alert>
        ) : (
          <>
            {/* ── Summary stat cards ── */}
            {summary && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
                {[
                  { label: "Total stays",   value: summary.total_stays ?? 0 },
                  { label: "Total nights",  value: summary.total_nights ?? 0 },
                  { label: "Total spent",   value: summary.total_spent != null ? `$${Number(summary.total_spent).toFixed(2)}` : "N/A" },
                  { label: "Avg stay",      value: summary.avg_stay_nights != null ? `${Number(summary.avg_stay_nights).toFixed(1)} nights` : "N/A" },
                  { label: "Last stay",     value: fmtDateLocal(summary.last_stay_date) },
                  ...(summary.most_used_slip ? [{ label: "Fav slip", value: summary.most_used_slip }] : []),
                ].map(({ label, value }) => (
                  <Paper key={label} elevation={0} sx={{ px: 2, py: 1.25, borderRadius: "12px", border: "1px solid rgba(47,99,173,0.1)", minWidth: 110 }}>
                    <Typography variant="caption" sx={{ color: "#9aa5b4", display: "block" }}>{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142", mt: 0.25 }}>{value}</Typography>
                  </Paper>
                ))}
              </Box>
            )}

            {/* ── Reservation timeline ── */}
            {reservations.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: "10px" }}>No reservation history yet.</Alert>
            ) : (
              <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid rgba(47,99,173,0.1)", overflow: "hidden" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      {["", "Dates", "Slip", "Type", "Status", "Charges", "Total"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#5f6b7a", py: 1.25, whiteSpace: "nowrap" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reservations.map((r) => {
                      const isOpen = expanded[r.id];
                      const sStyle = RES_STATUS_COLORS[r.status] || RES_STATUS_COLORS.confirmed;
                      const hasCharges = (r.charges || []).length > 0;
                      return (
                        <React.Fragment key={r.id}>
                          <TableRow hover
                            onClick={() => hasCharges && toggleRow(r.id)}
                            sx={{ cursor: hasCharges ? "pointer" : "default", "&:last-child td": { border: 0 } }}>
                            <TableCell sx={{ width: 28, pl: 1.5, pr: 0 }}>
                              {hasCharges && (
                                isOpen
                                  ? <ExpandLessIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                                  : <ExpandMoreIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                              {fmtDateLocal(r.start_date)}
                              <Typography variant="caption" sx={{ display: "block", color: "#9aa5b4" }}>
                                → {fmtDateLocal(r.end_date)}
                                {r.nights != null ? ` (${r.nights}n)` : ""}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                              {r.slip_number}
                              <Typography variant="caption" sx={{ display: "block", color: "#9aa5b4" }}>{r.dock_name}</Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem", color: "#6b778c", textTransform: "capitalize" }}>
                              {r.reservation_type}
                            </TableCell>
                            <TableCell>
                              <Chip label={r.status?.replace(/_/g, " ")} size="small"
                                sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, textTransform: "capitalize",
                                  backgroundColor: sStyle.bg, color: sStyle.color }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem", color: "#6b778c" }}>
                              {hasCharges ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <ReceiptLongIcon sx={{ fontSize: 14 }} />
                                  {r.charges.length}
                                </Box>
                              ) : "N/A"}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontFamily: "monospace", color: "#2f63ad" }}>
                              {r.total_amount != null ? `$${Number(r.total_amount).toFixed(2)}` : "N/A"}
                            </TableCell>
                          </TableRow>

                          {/* ── Expanded charges ── */}
                          {hasCharges && (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                  <Box sx={{ bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", px: 4, py: 1 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          {["Type", "Description", "Qty", "Unit Price", "Amount"].map((h) => (
                                            <TableCell key={h} sx={{ color: "#94a3b8", fontWeight: 700, fontSize: "0.7rem", py: 0.5, borderBottom: "1px solid #e2e8f0" }}>{h}</TableCell>
                                          ))}
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {r.charges.map((c) => (
                                          <TableRow key={c.id} sx={{ "&:last-child td": { border: 0 } }}>
                                            <TableCell>
                                              <Chip label={c.charge_type?.replace(/_/g, " ")} size="small"
                                                sx={{ height: 18, fontSize: "0.65rem", fontWeight: 600, textTransform: "capitalize",
                                                  backgroundColor: "rgba(47,99,173,0.08)", color: "#2f63ad" }} />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "0.8rem" }}>{c.description}</TableCell>
                                            <TableCell sx={{ fontSize: "0.78rem", color: "#6b778c" }}>{c.quantity}</TableCell>
                                            <TableCell sx={{ fontSize: "0.78rem", fontFamily: "monospace" }}>
                                              {c.unit_price != null ? `$${Number(c.unit_price).toFixed(2)}` : "N/A"}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: "monospace", fontWeight: 600, fontSize: "0.8rem",
                                              color: c.charge_type === "discount" ? "#dc2626" : "#223142" }}>
                                              {c.total_amount != null
                                                ? (c.charge_type === "discount" ? `-$${Number(c.total_amount).toFixed(2)}` : `$${Number(c.total_amount).toFixed(2)}`)
                                                : "N/A"}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Vessels table ──────────────────────────────────────────────────────── */
const VesselsTab = ({ marinaId, showSnack }) => {
  const perms = useContext(PermissionsContext);
  const canManage = perms.includes("marina.manage_vessels");

  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [historyTarget, setHistoryTarget] = useState(null);
  const debounce = useRef(null);

  const fetchVessels = useCallback(async (q = "", owner = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      // Do NOT filter by marina_id. vessels are global and have no marina until
      // a reservation is created. Filtering here hides newly added vessels.
      const res = await api.get(`/vessels?${params}`);
      const all = res.data || [];
      setVessels(owner ? all.filter((v) => v.owner_type === owner) : all);
    } catch { showSnack("Failed to load vessels", "error"); }
    finally { setLoading(false); }
  }, [showSnack]);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchVessels(search, ownerFilter), 350);
    return () => clearTimeout(debounce.current);
  }, [search, ownerFilter, fetchVessels]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vessel?")) return;
    try { await api.delete(`/vessels/${id}`); showSnack("Vessel deleted"); fetchVessels(search, ownerFilter); }
    catch { showSnack("Failed to delete vessel", "error"); }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", flex: 1 }}>
          <TextField size="small" placeholder="Search name, reg #, owner…" value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ ...fieldSx, minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} /></InputAdornment> }} />
          <FormControl size="small" sx={{ ...fieldSx, minWidth: 160 }}>
            <InputLabel>Owner Type</InputLabel>
            <Select value={ownerFilter} label="Owner Type" onChange={(e) => setOwnerFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="external">External</MenuItem>
              <MenuItem value="hoa_member">HOA Member</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} sx={primaryBtnSx} onClick={() => setDialog({ open: true, editing: null })}>
            Add Vessel
          </Button>
        )}
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid rgba(47,99,173,0.08)", boxShadow: "0 4px 14px rgba(0,0,0,0.04)", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                {["Vessel", "Type", "LOA × Beam", "Owner", "Reg #", "Insurance Exp.", "Reg. Exp.", "Current Slip", "History", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a", py: 1.25, whiteSpace: "nowrap" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 10 }).map((__, j) => <TableCell key={j}><Box sx={{ height: 16, backgroundColor: "#f0f0f0", borderRadius: 1 }} /></TableCell>)}</TableRow>
                ))
              ) : vessels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ textAlign: "center", py: 6 }}>
                    <DirectionsBoatIcon sx={{ fontSize: 40, color: "#d0d7e0", mb: 1, display: "block", mx: "auto" }} />
                    <Typography variant="body2" sx={{ color: "#9aa5b4" }}>No vessels found.</Typography>
                  </TableCell>
                </TableRow>
              ) : vessels.map((v) => {
                const insDays = daysUntil(v.insurance_expiry);
                const regDays = daysUntil(v.registration_expiry);
                const rowAlert = (insDays !== null && insDays <= 30) || (regDays !== null && regDays <= 30);
                return (
                  <TableRow key={v.id} hover sx={{ backgroundColor: rowAlert ? "rgba(217,119,6,0.03)" : "transparent", "&:last-child td": { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>{v.vessel_name}</Typography>
                      <Typography variant="caption" sx={{ color: "#9aa5b4" }}>{v.color || ""}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: "#445", textTransform: "capitalize" }}>{v.vessel_type || "N/A"}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: "#445" }}>{v.loa ? `${v.loa} × ${v.beam ?? "N/A"}` : "N/A"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{v.owner_display || "N/A"}</Typography>
                          <Chip label={v.owner_type === "hoa_member" ? "HOA" : "External"} size="small" sx={{ height: 18, fontSize: "0.68rem", fontWeight: 600, mt: 0.25, backgroundColor: v.owner_type === "hoa_member" ? "rgba(47,99,173,0.1)" : "rgba(107,119,140,0.1)", color: v.owner_type === "hoa_member" ? "#1d4ed8" : "#374151" }} />
                        </Box>
                        <OwnerInfoPopover
                          ownerType={v.owner_type}
                          ownerId={v.owner_id}
                          memberCode={v.property_id}
                          displayName={v.owner_display}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", fontFamily: "monospace", color: "#445" }}>{v.registration_number || "N/A"}</TableCell>
                    <TableCell><ExpiryChip dateStr={v.insurance_expiry} /></TableCell>
                    <TableCell><ExpiryChip dateStr={v.registration_expiry} /></TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: "#6b778c" }}>{v.current_slip || "N/A"}</TableCell>
                    <TableCell>
                      <Tooltip title="View history">
                        <IconButton size="small" sx={{ color: "#6b778c" }} onClick={() => setHistoryTarget(v)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <IconButton size="small" sx={{ color: "#2f63ad" }} onClick={() => setDialog({ open: true, editing: v })}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(v.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <VesselDialog
        open={dialog.open}
        editing={dialog.editing}
        onClose={() => setDialog({ open: false, editing: null })}
        onSaved={() => { fetchVessels(search, ownerFilter); showSnack(dialog.editing ? "Vessel updated" : "Vessel added"); }}
      />
      <VesselHistoryDialog
        open={Boolean(historyTarget)}
        vessel={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />
    </Box>
  );
};

/* ── Owners table ───────────────────────────────────────────────────────── */
const OwnersTab = ({ showSnack }) => {
  const perms = useContext(PermissionsContext);
  const canManage = perms.includes("marina.manage_vessels");

  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const debounce = useRef(null);

  const fetchOwners = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const res = await api.get(`/vessel-owners${q ? `?search=${encodeURIComponent(q)}` : ""}`);
      setOwners(res.data || []);
    } catch { showSnack("Failed to load owners", "error"); }
    finally { setLoading(false); }
  }, [showSnack]);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchOwners(search), 350);
    return () => clearTimeout(debounce.current);
  }, [search, fetchOwners]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this owner?")) return;
    try { await api.delete(`/vessel-owners/${id}`); showSnack("Owner deleted"); fetchOwners(search); }
    catch { showSnack("Failed to delete owner", "error"); }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
        <TextField size="small" placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ ...fieldSx, minWidth: 260 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} /></InputAdornment> }} />
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} sx={primaryBtnSx} onClick={() => setDialog({ open: true, editing: null })}>
            Add Owner
          </Button>
        )}
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid rgba(47,99,173,0.08)", boxShadow: "0 4px 14px rgba(0,0,0,0.04)", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                {["Name", "Email", "Phone", "City / State", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a", py: 1.25 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 5 }).map((__, j) => <TableCell key={j}><Box sx={{ height: 16, backgroundColor: "#f0f0f0", borderRadius: 1 }} /></TableCell>)}</TableRow>)
              ) : owners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", py: 6 }}>
                    <PersonIcon sx={{ fontSize: 40, color: "#d0d7e0", mb: 1, display: "block", mx: "auto" }} />
                    <Typography variant="body2" sx={{ color: "#9aa5b4" }}>No external owners found.</Typography>
                  </TableCell>
                </TableRow>
              ) : owners.map((o) => (
                <TableRow key={o.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                  <TableCell sx={{ fontWeight: 600, color: "#223142" }}>{o.first_name} {o.last_name}</TableCell>
                  <TableCell sx={{ fontSize: "0.82rem", color: "#445" }}>{o.email || "N/A"}</TableCell>
                  <TableCell sx={{ fontSize: "0.82rem", color: "#445" }}>{o.phone || "N/A"}</TableCell>
                  <TableCell sx={{ fontSize: "0.8rem", color: "#6b778c" }}>{o.city ? `${o.city}, ${o.state}` : "N/A"}</TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <IconButton size="small" sx={{ color: "#2f63ad" }} onClick={() => setDialog({ open: true, editing: o })}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(o.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <OwnerDialog
        open={dialog.open}
        editing={dialog.editing}
        onClose={() => setDialog({ open: false, editing: null })}
        onSaved={() => { fetchOwners(search); showSnack(dialog.editing ? "Owner updated" : "Owner added"); }}
      />
    </Box>
  );
};

/* ── Main component ─────────────────────────────────────────────────────── */
const MarinaVessels = ({ marinaId, showSnack }) => {
  const [innerTab, setInnerTab] = useState(0);
  return (
    <Box>
      <Tabs value={innerTab} onChange={(_, v) => setInnerTab(v)} sx={{ mb: 3, borderBottom: "1px solid #eef2f7", "& .MuiTab-root": { textTransform: "none", fontWeight: 600 } }}>
        <Tab label="Vessels" />
        <Tab label="External Owners" />
      </Tabs>
      {innerTab === 0 && <VesselsTab marinaId={marinaId} showSnack={showSnack} />}
      {innerTab === 1 && <OwnersTab showSnack={showSnack} />}
    </Box>
  );
};

export default MarinaVessels;
